'use client';

import { useSession } from '@shared/auth';
import { Slots } from '@shared/icons';
import { ROUTES, useClientConfig } from '@shared/utils';
import { LoginOrSignUp, type LoginOrSignUpProps } from '../../LoginOrSignUp/LoginOrSignUp';
import type { GameLaunchInterceptor, LaunchChannel } from '../GameLauncher/types';
import { GameLauncherWrapper } from '../GameLauncherWrapper/GameLauncherWrapper';
import { GamePlayPageComponent } from '../GamePlay/GamePlayPageComponent';
import type { SimilarGamesCarouselFilter } from '../SimilarGamesCarousel/SimilarGamesCarousel';
import { useGamePlayPageLayout } from './hooks/useGamePlayPageLayout/useGamePlayPageLayout';

type GamePlayPageCategoryFilters = {
  for_you: SimilarGamesCarouselFilter;
};

type GamePlayPageProps = {
  params: { gameName: string };
  searchParams: { [key: string]: string | string[] | undefined };
  categoryFilters: GamePlayPageCategoryFilters;
  lobbyPath?: string;
  loginOrSignUpOptions?: LoginOrSignUpProps;
  launchInterceptor?: GameLaunchInterceptor;
  launchChannel?: LaunchChannel;
};

const defaultLoginOrSignUpOptions: LoginOrSignUpProps = {
  icon: Slots,
  namespace: 'game-launcher.login_or_signup',
};

function GamePlayPage({ params, searchParams, categoryFilters, lobbyPath = ROUTES.CASINO.DISCOVERY, loginOrSignUpOptions, launchInterceptor, launchChannel }: GamePlayPageProps) {
  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';
  const { gameName } = params;
  const { inIframe } = searchParams;
  const isInIframe = inIframe === 'true';
  const layout = useGamePlayPageLayout(inIframe);
  const config = useClientConfig();
  const carouselCategory = categoryFilters.for_you;

  if (!isLoggedIn) {
    return <LoginOrSignUp {...defaultLoginOrSignUpOptions} {...loginOrSignUpOptions} />;
  }

  return (
    <GamePlayPageComponent carouselCategory={carouselCategory} gameName={gameName} layout={layout}>
      <GameLauncherWrapper
        gameName={gameName}
        baseUrl={config.baseUrl}
        gameMode={config.gameMode}
        carouselCategory={carouselCategory}
        inIframe={isInIframe}
        lobbyPath={lobbyPath}
        layout={layout}
        launchInterceptor={launchInterceptor}
        launchChannel={launchChannel}
      />
    </GamePlayPageComponent>
  );
}

export { GamePlayPage, type GamePlayPageCategoryFilters, type GamePlayPageProps };
